import { GlobalHelperFunctions } from '../../../../utils/GlobalHelperFunctions';
import './style.scss';

export default class ArchivePanel {
  mDiv: JQuery<HTMLElement>;
  private _constructorParams: any;
  constructor(aConstructorParams: any) {
    this._constructorParams = aConstructorParams;
    this.mDiv = this._generateHtml();
    this._addRefreshButton();
    this._getArchivesForSession(this._constructorParams.content.Id);
  }

  private _generateHtml(): JQuery<HTMLElement> {
    return $(
      HGEN.tree({
        style: 'archive_panel',
        children: [
          {
            style: 'archive_panel_header',
            children: [
              {
                style: '',
                interior: CrowdHub_App.LANG.ARCHIVE_HEADER,
              },
              { id: 'refresh_archive_button', style: '' },
            ],
          },
          { id: 'archive-table', style: 'archive_table' },
        ],
      }),
    );
  }

  private _addRefreshButton(): void {
    var aButton = new BaseCSSButton();
    aButton.initialize({
      textStyle: '',
      buttonStyle: 'standard_button_filled',
      text: 'Refresh',
      click: this._refreshArchives.bind(this),
      scope: this,
      showLoader: true,
      container: this.mDiv.find('#refresh_archive_button'),
    });
  }

  private _refreshArchives(): void {
    this.mDiv.find('#archive-table').empty();
    this._getArchivesForSession(this._constructorParams.content.Id);
  }

  private _getArchivesForSession(contentId: number): any {
    var aVars = {
      api: 'api_toxbox_archive',
      action: 'LIST',
      content_id: contentId,
    };

    App.INSTANCE.mApi.Request(
      CrowdHub_App.CONFIG.BASE_URL + 'custom_api/api_Services_Opentok.php',
      aVars,
      aResult => {
        if (aResult.success == 'true') {
          this._showArchives(aResult.archives);
        } else {
          this.mDiv.find('.archive_panel').hide();
          console.error(aResult);
        }
      },
      aError => {
        console.error(aError);
      },
    );
  }

  private _showArchives(archiveInfo): void {
    var archiveTable = this.mDiv.find('#archive-table');

    if (archiveInfo.length == 0) {
      var noArchivesHtml = $(
        HGEN.tree({
          style: 'no_archives',
          interior: 'No Archives Available',
        }),
      );
      archiveTable.append(noArchivesHtml);
    }

    archiveInfo.forEach(archive => {
      // build the html and append to the table
      var archiveRow = this._buildHtmlRow(archive);
      archiveTable.append(archiveRow);

      var aParams = {
        user_id: App.INSTANCE.CurrentUser.Id,
        token: App.INSTANCE.CurrentUser.Token,
        file: archive.LOCAL,
        filename: archive.name + '.mp4',
      };

      // build the video player preview and append to the row
      var player = this._buildVideoPlayerForRow(aParams);
      player.mVideoPlayer.appendPlayerTo(archiveRow.find('.row_preview_container'));

      // need to manually set controls here so we get the download button
      player.mVideoPlayer.mDiv.find('video').attr('controls', 'controls');
      player.mVideoPlayer.mDiv.find('video').removeAttr('controlsList');

      // sometimes they fail
      if (archive.size == 0) {
        archiveRow.find('#archive-size').addClass('failed');
      }

      Accessibility.clickAndKeypress(
        archiveRow.find('.preview_button'),
        () => {
          archiveRow.find('.table_row_body').toggle();
        },
        false,
      );
    });
  }

  private _buildHtmlRow(archive): JQuery<HTMLElement> {
    /**
     * trying to split the archive name into the name and timestamp.
     * As of 10/10/21 archive names are set with a timestamp at the end instead of a date string
     * example: Test Room 1633877721
     * ! NOTE: the timestamp is set in php meaning you must multiply by 1000 to convert to javascript timestamp
     * PRE this change, the complexity of attempting to try to split out a date string from a name isn't worth it so we don't attempt to parse.
     */
    var potentialTimeSplit = archive.name.slice(archive.name.length - 10, archive.name.length);
    var archiveStartDate = new Date(potentialTimeSplit * 1000);
    var archiveStart = 'Unable to convert start date.';
    var archiveName = archive.name;

    var archiveDuration = GlobalHelperFunctions.generateTimeFromSeconds(archive.duration);

    if (archiveStartDate.toString() != 'Invalid Date') {
      archiveStart = moment(potentialTimeSplit * 1000)
        .local()
        .format('YYYY/MM/DD hh:mm:ss a');
      archiveName = archive.name.slice(0, archive.name.length - 10);
    }

    var archiveRow = $(
      HGEN.tree({
        style: 'table_row',
        children: [
          {
            style: 'table_row_header',
            children: [
              {
                style: 'table_row_primary',
                children: [
                  {
                    style: 'table_row_header_text',
                    interior: archiveName,
                  },
                ],
              },
              {
                style: 'table_row_primary',
                children: [
                  {
                    style: 'table_row_header_label',
                    interior: `Started: ${archiveStart}`,
                  },
                  {
                    style: 'table_row_header_label',
                    interior: `Upload Status: Complete`,
                    if: archive.status == 'uploaded',
                  },
                  {
                    style: 'table_row_header_label',
                    interior: `Video Duration: ${archiveDuration}`,
                  },
                ],
              },
              {
                style: 'table_row_primary',
                children: [
                  {
                    style: 'table_row_header_text preview_button',
                    interior: `Preview`,
                  },
                ],
              },
            ],
          },
          {
            style: 'table_row_body',
            children: [
              {
                style: 'table_row_details',
                children: [
                  {
                    style: 'table_row_details_left',
                    children: [
                      {
                        style: 'table_row_header_text',
                        interior: `Resolution: ${archive.resolution}`,
                      },
                      {
                        id: 'archive-size',
                        style: 'table_row_header_text',
                        interior: `Size: ${Number(archive.size / 1000).toFixed(2)} KB`,
                      },
                      {
                        style: 'table_row_header_text',
                        interior: `ID: ${archive.id}`,
                      },
                    ],
                  },
                  {
                    style: 'table_row_details_right',
                    children: [{ style: 'row_preview_container' }],
                  },
                ],
              },
            ],
          },
          { style: 'table_row_footer' },
        ],
      }),
    );
    return archiveRow;
  }

  private _buildVideoPlayerForRow(videoPlayerParams): StreamVideoPlayer {
    var aQuery = GlobalHelperFunctions.createUrlQueryString(videoPlayerParams);
    var aUrl = `${CrowdHub_App.CONFIG.BASE_URL}custom_api/api_download_secureds3Object.php`;
    var aDownloadLink = `${aUrl}?${aQuery}`;

    let player = new StreamVideoPlayer({
      autoPlay: false,
      content: this._constructorParams.content,
      contentType: 'video/mp4',
      clickText: '',
      controls: true,
      fillMode: 'aspect',
      preLoad: 'none',
      //TODO: add some data to these function?
      onPause: function () {},
      onWatchedSome: function () {},
      onWatchedHalf: function () {},
      onWatched: function () {},
      onTimeChanged: function () {},
      onWatchedFully: function () {},
      start: 0,
      videoId: aDownloadLink,
      videoPlayerClass: VideoJSPlayer,
      videoUrl: aDownloadLink,
    });

    return player;
  }
}
